import { TranslateFunction } from '@libs/focus-frontend-i18n/src';
import {
  getDateFormatShort,
  getDateFormatShortWithTime,
} from '@libs/focus-frontend-services/src/PreferencesService/PreferencesService';
import { getLanguageWithDevFallback } from '@libs/focus-frontend-utils/src/language';
import { i18n } from 'i18next';
import memoize from 'lodash/memoize';
import {
  DateObjectUnits,
  DateTime,
  DateTimeJSOptions,
  Duration,
  DurationLikeObject,
  DurationOptions,
  DurationUnit,
  Interval,
  Zone,
} from 'luxon';

import { UserPreferredShortDateFormat } from './types';

// the preferences don't match luxon's format 100%, hence this map
const mapUserPreferenceToLuxon = [
  { match: 'yyyy', replacement: 'yyyy' }, // four-digit year, e.g. 1996
  { match: 'mmmm', replacement: 'LLLL' }, // month spelled out in full, e.g. April
  { match: 'dddd', replacement: 'cccc' }, // day of the week spelled out in full, e.g. Tuesday
  { match: 'mmm', replacement: 'LLL' }, // three-letter abbreviation for month, e.g. Apr
  { match: 'ddd', replacement: 'ccc' }, // three-letter abbreviation for day of the week, e.g. Tue
  { match: 'yy', replacement: 'yy' }, // two-digit year, e.g. 96
  { match: 'mm', replacement: 'LL' }, // two-digit month, e.g. 04
  { match: 'dd', replacement: 'dd' }, // two-digit day of the month, e.g. 02
  { match: 'm', replacement: 'L' }, // one-digit month for months below 10, e.g. 4
  { match: 'd', replacement: 'd' }, // one-digit day of the month for days below 10, e.g. 2
];

export const preferenceToLuxonFormat = memoize((preferredFormat: string) => {
  return preferredFormat
    .split(' ')
    .map((part) =>
      part.includes(':') // time part can not be mapped (mm for minutes can not be mapped to LL)
        ? part
        : mapUserPreferenceToLuxon.reduce(
            (accumulator, { match, replacement }) => accumulator.replace(match, replacement),
            part,
          ),
    )
    .join(' ');
});

export const toISO = (date: DateTime) => date.toISO()!;
export const toISODate = (date: DateTime) => date.toISODate()!;
export const toISODateWithoutMilliseconds = (date: DateTime) => date.toFormat("yyyy-LL-dd'T'HH:mm:ssZZ");

export const TIME_STRING = 'HH:mm:ss';

export const fromISO = (date?: string | null) => DateTime.fromISO(date ?? '');
export const fromFormat = (date: string, format?: string) => {
  const selectedFormat = format || getDateFormatShort();

  return DateTime.fromFormat(date, preferenceToLuxonFormat(selectedFormat));
};
export const fromObject = (object: DateObjectUnits, options?: DateTimeJSOptions) =>
  DateTime.fromObject(object, options);
export const fromTimeString = (date: string) => DateTime.fromFormat(date, TIME_STRING);

export const toFormat = (date: DateTime | Duration | Interval | string, format?: string) => {
  const selectedFormat = format || getDateFormatShort();
  const language = getLanguageWithDevFallback();
  const isISODateString = typeof date === 'string';
  const dateTimeObject = isISODateString ? fromISO(date) : date;

  return dateTimeObject?.toFormat(preferenceToLuxonFormat(selectedFormat), { locale: language });
};

export const toShortYearInterval = (start: DateTime, end: DateTime) => {
  const dayAndMonthFormat = getDateFormatShort().replace(/(\D)\w{4}/g, ''); // remove /yyyy, .yyyy or -yyyy so we only have dd/mm, mm/dd, dd.mm or dd-mm

  if (start.year === end.year) {
    return `${toFormat(start, dayAndMonthFormat)} - ${toFormat(end)}`;
  } else {
    return `${toFormat(start)} - ${toFormat(end)}`;
  }
};

export const toFormattedDateTimeString = (dateTime: DateTime | string, format?: string) => {
  const selectedFormat = format || getDateFormatShortWithTime();

  return toFormat(dateTime, selectedFormat);
};

export const startOfDay = (date: DateTime) => date.startOf('day');
export const endOfDay = (date: DateTime) => date.endOf('day');
export const startOfYear = (date: DateTime) => date.startOf('year');
export const endOfYear = (date: DateTime) => date.endOf('year');
export const startOfWeek = (date: DateTime) => date.startOf('week');
export const endOfWeek = (date: DateTime) => date.endOf('week');
export const startOfMonth = (date: DateTime) => date.startOf('month');
export const endOfMonth = (date: DateTime) => date.endOf('month');
export const startOfPreviousMonth = (date: DateTime) => date.minus({ month: 1 }).startOf('month');
export const endOfPreviousMonth = (date: DateTime) => date.minus({ month: 1 }).endOf('month').startOf('day');
export const startOfQuarter = (date: DateTime) => date.startOf('quarter');
export const endOfQuarter = (date: DateTime) => date.endOf('quarter');
export const startOfPreviousQuarter = (date: DateTime) => date.minus({ quarter: 1 }).startOf('quarter');
export const endOfPreviousQuarter = (date: DateTime) => date.minus({ quarter: 1 }).endOf('quarter').startOf('day');
export const startOfPreviousYear = (date: DateTime) => date.minus({ year: 1 }).startOf('year');
export const endOfPreviousYear = (date: DateTime) => date.minus({ year: 1 }).endOf('year').startOf('day');

export const addYears = (date: DateTime, years?: number) => date.plus({ years });
export const subtractYears = (date: DateTime, years?: number) => date.minus({ years });

export const addMonths = (date: DateTime, months?: number) => date.plus({ months });
export const subtractMonths = (date: DateTime, months?: number) => date.minus({ months });

export const addDays = (date: DateTime, days?: number) => date.plus({ days });
export const subtractDays = (date: DateTime, days?: number) => date.minus({ days });

export const addHours = (date: DateTime, hours?: number) => date.plus({ hours });
export const subtractHours = (date: DateTime, hours?: number) => date.minus({ hours });

export const addMinutes = (date: DateTime, minutes?: number) => date.plus({ minutes });
export const subtractMinutes = (date: DateTime, minutes?: number) => date.minus({ minutes });

export const addSeconds = (date: DateTime, seconds?: number) => date.plus({ seconds });
export const subtractSeconds = (date: DateTime, seconds?: number) => date.minus({ seconds });

export const isBefore = (date: DateTime, dateToCompare: DateTime) => date.valueOf() < dateToCompare.valueOf();
export const isAfter = (date: DateTime, dateToCompare: DateTime) => date.valueOf() > dateToCompare.valueOf();

export const isSameDay = (firstDate: DateTime, secondDate: DateTime) => firstDate.hasSame(secondDate, 'day');
export const isToday = (date: DateTime) => isSameDay(DateTime.local(), date);

export const isTomorrow = (date: DateTime) => isSameDay(addDays(DateTime.local(), 1), date);
export const isYesterday = (date: DateTime) => isSameDay(subtractDays(DateTime.local(), 1), date);

export const min = (...dates: DateTime[]) => DateTime.min(...dates);
export const max = (...dates: DateTime[]) => DateTime.max(...dates);
export const differenceInSeconds = (firstDate: DateTime, secondDate: DateTime) => firstDate.diff(secondDate, 'seconds');
export const differenceInMinutes = (firstDate: DateTime, secondDate: DateTime) => firstDate.diff(secondDate, 'minutes');
export const differenceInDays = (firstDate: DateTime, secondDate: DateTime) => firstDate.diff(secondDate, 'days');
export const differenceInWeeks = (firstDate: DateTime, secondDate: DateTime) => firstDate.diff(secondDate, 'weeks');
export const asMinutes = (duration: Duration) => duration.as('minutes');
export const asDays = (duration: Duration) => duration.as('days');

export const toLocaleStringTime24Simple = (date: DateTime) => date.toLocaleString(DateTime.TIME_24_SIMPLE);
export const toLocaleStringDateMed = (date: DateTime) => date.toLocaleString(DateTime.DATE_MED);
export const toLocaleStringDateFull = (date: DateTime) => date.toLocaleString(DateTime.DATE_FULL);

export const toTimeString = (date: DateTime) => date.toFormat(TIME_STRING);

export const getDay = (date: DateTime) => date.day;
export const getWeekdayLong = (date: DateTime) => toFormat(date, 'cccc');
export const getWeekdayShort = (date: DateTime) => toFormat(date, 'ccc');
export const getWeekday = (date: DateTime) => date.weekday;
export const getMonth = (date: DateTime) => date.month;
export const getMonthLong = (date: DateTime) => toFormat(date, 'LLLL');
export const getYear = (date: DateTime) => date.year;

export const formatMinutesToHoursAndMinutes = (amountOfMinutes = 0, dividerHours = 'h', dividerMinutes = 'm') => {
  const hours = Math.floor(amountOfMinutes / 60);
  const minutes = amountOfMinutes % 60;
  let formattedHours = '';
  let formattedMinutes = '';

  if (hours) {
    formattedHours = `${hours}${dividerHours}`;
  }

  if (minutes) {
    formattedMinutes = `${minutes}${dividerMinutes}`;
  }

  return `${formattedHours}${formattedMinutes}`;
};

export const getDateFormatPlaceholder = (translate: TranslateFunction) => {
  const userSelectedFormat = getDateFormatShort();
  const placeholderConfig: Record<UserPreferredShortDateFormat, string> = {
    [UserPreferredShortDateFormat.DefaultFormat]: translate('date.placeholder'),
    [UserPreferredShortDateFormat.DotFormat]: translate('date.placeholderWithDot'),
    [UserPreferredShortDateFormat.HyphenFormat]: translate('date.placeholderWithHyphen'),
    [UserPreferredShortDateFormat.MonthFirstFormat]: translate('date.placeholderWithMonthFirst'),
  };

  return placeholderConfig[userSelectedFormat as UserPreferredShortDateFormat];
};

const getI18nResource = (i18n: i18n) => (key: string) =>
  i18n.languages.reduce<string>((translation, language) => {
    if (translation) {
      return translation;
    }
    return i18n.getResource(language, 'translations', key) || '';
  }, '');

const getTimeAgoTranslations = (i18n: i18n) => {
  const getResource = getI18nResource(i18n);
  const translations = {
    justNow: getResource('justNow'),
    singleMinuteAgo: getResource('singleMinuteAgo'),
    multipleMinutesAgo: getResource('multipleMinutesAgo'),
    singleHourAgo: getResource('singleHourAgo'),
    multipleHoursAgo: getResource('multipleHoursAgo'),
    singleDayAgo: getResource('singleDayAgo'),
    multipleDaysAgo: getResource('multipleDaysAgo'),
    singleWeekAgo: getResource('singleWeekAgo'),
    multipleWeeksAgo: getResource('multipleWeeksAgo'),
    onDate: getResource('onDate'),
  };

  return translations;
};

export const timeAgo = (firstDate: DateTime, secondDate: DateTime, i18n: i18n) => {
  const translations = getTimeAgoTranslations(i18n);
  const interpolate = (string: string, amount: number) => string.replace('{{amount}}', amount.toString());

  if (isSameDay(firstDate, secondDate)) {
    const difference = firstDate.diff(secondDate);
    const differenceInSeconds = difference.as('seconds');
    const differenceInMinutes = Math.floor(difference.as('minutes'));
    const differenceInHours = Math.floor(difference.as('hours'));
    return (
      (differenceInSeconds < 60 && translations.justNow) ||
      (differenceInSeconds < 120 && interpolate(translations.singleMinuteAgo, 1)) ||
      (differenceInSeconds < 3600 && interpolate(translations.multipleMinutesAgo, differenceInMinutes)) ||
      (differenceInSeconds < 7200 && interpolate(translations.singleHourAgo, 1)) ||
      (differenceInSeconds < 86400 && interpolate(translations.multipleHoursAgo, differenceInHours)) ||
      ''
    );
  } else {
    const difference = startOfDay(firstDate).diff(startOfDay(secondDate));
    const differenceInDays = Math.floor(difference.as('days'));
    const differenceInWeeks = Math.floor(difference.as('weeks'));

    if (differenceInDays < 0 || differenceInDays >= 31) {
      return translations.onDate.replace('{{date}}', toLocaleStringDateMed(secondDate));
    } else if (differenceInDays === 1) {
      return translations.singleDayAgo;
    } else if (differenceInDays < 7) {
      return interpolate(translations.multipleDaysAgo, differenceInDays);
    } else if (differenceInWeeks === 1) {
      return interpolate(translations.singleWeekAgo, differenceInWeeks);
    } else if (differenceInDays < 31) {
      return interpolate(translations.multipleWeeksAgo, differenceInWeeks);
    }
  }
};

export const timeAgoFromNow = (date: DateTime, i18n: i18n) => timeAgo(DateTime.fromJSDate(new Date()), date, i18n);
export const fromJSDate = (date?: Date, options?: { zone?: string | Zone }) => DateTime.fromJSDate(date!, options);
export const toJSDate = (date: DateTime) => date.toJSDate();

export const dateTimeToPaddedHM = (date: DateTime) => (date ? date.toFormat('HH:mm') : '00:00');
export const durationToPaddedHM = (duration?: Duration) => (duration ? duration.toFormat('hh:mm') : '00:00');
export const durationToPaddedHMS = (duration?: Duration) => (duration ? duration.toFormat('hh:mm:ss') : '00:00:00');
export const durationWithHourMark = (duration?: Duration) =>
  duration ? duration.toFormat('h:mm').replace(':', 'h') : '0h00';
export const durationWithHourMarkFull = (duration?: Duration | null) =>
  duration ? duration.toFormat('hh:mm').replace(':', 'H') : '00H00';
export const fromPaddedHM = (time: string) => fromFormat(time, 'HH:mm');

export const durationToHM = (duration?: Duration) => (duration ? duration.toFormat('h:mm') : '0:00');
export const secondsToHM = (seconds?: number) =>
  seconds ? Duration.fromMillis(seconds * 1000).toFormat('h:mm') : '0:00';
export const secondsToHours = (seconds: number) => Math.round((seconds / 3600 + Number.EPSILON) * 100) / 100;

export const durationToShortPaddedHM = (translate: TranslateFunction, duration?: Duration) =>
  duration ? duration.toFormat(`hh'${translate('hourShort')}'mm`) : `00${translate('hourShort')}00`;
export const secondsToShortPaddedHM = (translate: TranslateFunction, seconds?: number) => {
  const time = seconds
    ? Duration.fromMillis(Math.abs(seconds) * 1000).toFormat(`hh'${translate('hourShort')}'mm`)
    : null;
  return seconds && time ? (seconds < 0 ? `-${time}` : time) : `00${translate('hourShort')}00`;
};
export const secondsToShortHM = (translate: TranslateFunction, seconds = 0) => {
  const time = Duration.fromMillis(Math.abs(seconds) * 1000).toFormat(`h'${translate('hourShort')}'mm`);
  return seconds < 0 ? `-${time}` : time;
};

export const secondsToShortestHM = (translate: TranslateFunction, seconds?: number) => {
  if (!seconds) {
    return `0${translate('hourShort')}`;
  }

  const duration = Duration.fromMillis(seconds * 1000);
  return duration.toFormat('h:mm').split(':')[1] === '00'
    ? duration.toFormat(`h'${translate('hourShort')}`)
    : duration.toFormat(`h'${translate('hourShort')}'mm`);
};

export const set = (date: DateTime, toSetObject: DateObjectUnits) => date.set(toSetObject);

export const fromMillis = (ms: number) => DateTime.fromMillis(ms);
export const toMillis = (date: DateTime) => date.toMillis();

export const fromSeconds = (seconds: number) => DateTime.fromSeconds(seconds);
export const toSeconds = (date: DateTime) => date.toSeconds();
export const fromMinutes = (minutes: number) => fromSeconds(minutes * 60);

// gets the current date without the time
export const getCurrentDate = () => {
  const today = DateTime.local();
  return fromObject({ year: today.year, month: today.month, day: today.day });
};

export const durationFromSeconds = (seconds: number) => Duration.fromMillis(seconds * 1000);
export const toTimeOffset = (date: DateTime, offset: number) => date.toUTC(offset * 60);
export const durationFromObject = (object: DurationLikeObject, options?: DurationOptions) =>
  Duration.fromObject(object, options);
export const shiftTo = (duration: Duration, ...units: DurationUnit[]) => duration.shiftTo(...units);

export const diffInDays = (date1: DateTime, date2?: DateTime) => {
  date2 = date2 || DateTime.local();

  // change to fixed time, so we only compare days
  date2 = date2.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  date1 = date1.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  return date2.diff(date1, 'days').as('days');
};

export const getOffset = (date: DateTime) => date.toFormat('ZZ');
export const getNow = () => DateTime.local();

export const dateToWeekday = (date: Date) =>
  Intl.DateTimeFormat(getLanguageWithDevFallback(), { weekday: 'long' }).format(date);

export const getDatesInRange = (startDate: DateTime, endDate: DateTime, includeWeekends = true) => {
  const weekDates = [];

  let currentDay = startDate;
  while (currentDay <= endDate) {
    if (includeWeekends || currentDay.weekday < 6) {
      weekDates.push(currentDay);
    }

    currentDay = currentDay.plus({ days: 1 });
  }

  return weekDates;
};

export const getMonthsInRange = (startDate: DateTime, endDate: DateTime) => {
  const months: DateTime[] = [];

  let currentMonth = startDate.startOf('month');
  while (currentMonth <= endDate) {
    months.push(currentMonth);
    currentMonth = currentMonth.plus({ month: 1 });
  }

  return months;
};

export const getWeeksInRange = (startDate: DateTime, endDate: DateTime) => {
  const weeks: DateTime[] = [];

  let currentMonth = startDate.startOf('week');
  while (currentMonth <= endDate) {
    weeks.push(currentMonth);
    currentMonth = currentMonth.plus({ week: 1 });
  }

  return weeks;
};

export const getYearsInRange = (startDate: DateTime, endDate: DateTime) => {
  const years: DateTime[] = [];

  let currentYear = startDate.startOf('year');
  while (currentYear <= endDate) {
    years.push(currentYear);
    currentYear = currentYear.plus({ year: 1 });
  }

  return years;
};

export const getQuartersInRange = (startDate: DateTime, endDate: DateTime) => {
  const quarters: DateTime[] = [];

  let currentQuarter = startDate.startOf('quarter');
  while (currentQuarter <= endDate) {
    quarters.push(currentQuarter);
    currentQuarter = currentQuarter.plus({ quarter: 1 });
  }

  return quarters;
};
